/**
 * Algoritmo Minimax con Alpha-Beta Pruning
 * Per la dama internazionale 10x10
 *
 * Features:
 * - Iterative deepening con time limit
 * - Quiescence search (catture forzate ai nodi foglia)
 * - Move ordering migliorato
 */

import type { BoardState, Move, Piece } from '@draughts/shared';
import { Color, PieceType, GameStatus, VARIANT_CONFIGS } from '@draughts/shared';
import { getAllLegalMoves } from '../moves/capture-moves.js';
import { evaluatePosition } from './evaluation.js';
import { serializePosition, determineGameStatus, updateFrisianState, nextHalfMoveClock } from '../game/move-execution.js';

// Valori per posizioni terminali
const INFINITY = 1000000;
const WIN_SCORE = 100000;

/** Max time per search in ms (per difficulty level) */
const TIME_LIMITS: Record<number, number> = {
  2: 500,     // Very Easy
  4: 1000,    // Easy
  5: 1500,    // Easy-Medium
  6: 2000,    // Medium
  7: 3000,    // Medium-Hard
  8: 4000,    // Hard
  10: 8000,   // Master
};

interface SearchResult {
  score: number;
  move: Move | null;
  nodesSearched: number;
}

interface SearchContext {
  startTime: number;
  timeLimit: number;
  nodesSearched: number;
  timedOut: boolean;
}

/** Mossa migliore + la sua valutazione (score dal punto di vista del Bianco). */
export interface ScoredMove {
  move: Move | null;
  score: number;
}

/** Opzioni della ricerca a budget di tempo (`findBestMovesScoredTimed`). */
export interface ScoredSearchOptions {
  /** Budget wall-clock per l'INTERA ricerca: tutte le radici, tutte le iterazioni. */
  timeBudgetMs: number;
  /** Chiamata dopo ogni profondità COMPLETATA. Serve a mostrare un progresso live. */
  onIteration?: (depth: number, candidates: ScoredMove[]) => void;
}

/** Esito di `findBestMovesScoredTimed`: i candidati e quanto in là si è arrivati davvero. */
export interface ScoredMoveSet {
  candidates: ScoredMove[];
  /** Profondità più profonda COMPLETATA per intero (0 se non si è concluso alcun giro). */
  reachedDepth: number;
  /** true se la scala si è fermata prima di `maxDepth`. */
  timedOut: boolean;
}

/** Crescita di costo per ply assunta finché non se ne misura una: ~×4 con questo ordinamento. */
const DEFAULT_PLY_GROWTH = 4;
const MIN_PLY_GROWTH = 2;
const MAX_PLY_GROWTH = 8;
/** Sotto questa soglia (ms) il rapporto fra due iterazioni è solo rumore di clock. */
const GROWTH_SAMPLE_MIN_MS = 8;

/**
 * Ricerca interna: restituisce mossa migliore E score (prospettiva Bianco).
 * Usa iterative deepening con time limit per profondità >= 7.
 */
function searchBest(board: BoardState, depth: number): ScoredMove {
  // Mossa forzata: se alla radice esiste UNA sola mossa legale (di norma una cattura
  // obbligata), giocala subito senza avviare la ricerca. Evita ~3-4s a Expert (depth 8)
  // e fino a ~2s ai livelli intermedi. Vale per tutti i chiamanti (findBestMove +
  // findBestMoveWithScore).
  if (board.status === GameStatus.PLAYING) {
    const rootMoves = getAllLegalMoves(board);
    if (rootMoves.length === 1) {
      const only = rootMoves[0]!;
      return { move: only, score: evaluateTerminal(applyMoveToBoard(board, only)) };
    }
  }

  const isMaximizing = board.currentTurn === Color.WHITE;

  // Per profondità basse (<=6), ricerca diretta senza time limit
  if (depth <= 6) {
    const ctx: SearchContext = {
      startTime: Date.now(),
      timeLimit: TIME_LIMITS[depth] || 2000,
      nodesSearched: 0,
      timedOut: false,
    };
    const result = minimax(board, depth, -INFINITY, INFINITY, isMaximizing, ctx);
    return { move: result.move, score: result.score };
  }

  // Per profondità alte, iterative deepening con time limit
  const timeLimit = TIME_LIMITS[depth] || 5000;
  const startTime = Date.now();

  // Fallback: ricerca rapida a depth 2 per garantire una mossa valida
  const fallbackCtx: SearchContext = {
    startTime,
    timeLimit,
    nodesSearched: 0,
    timedOut: false,
  };
  const fallbackResult = minimax(board, 2, -INFINITY, INFINITY, isMaximizing, fallbackCtx);
  let bestMove: Move | null = fallbackResult.move;
  let bestScore = fallbackResult.score;

  for (let d = 4; d <= depth; d += 1) {
    const ctx: SearchContext = {
      startTime,
      timeLimit,
      nodesSearched: 0,
      timedOut: false,
    };

    const result = minimax(board, d, -INFINITY, INFINITY, isMaximizing, ctx);

    if (!ctx.timedOut && result.move) {
      bestMove = result.move;
      bestScore = result.score;
    }

    // Se il tempo è scaduto, usa il risultato della profondità precedente
    if (ctx.timedOut || Date.now() - startTime > timeLimit * 0.8) {
      break;
    }
  }

  return { move: bestMove, score: bestScore };
}

/**
 * Trova la mossa migliore per il giocatore corrente
 * Usa iterative deepening con time limit per profondità >= 7
 */
export function findBestMove(board: BoardState, depth: number = 4): Move | null {
  return searchBest(board, depth).move;
}

/**
 * Come findBestMove, ma restituisce anche la valutazione della posizione
 * (score dal punto di vista del Bianco: >0 favorevole al Bianco, <0 al Nero).
 * Usato dai bot per decisioni realistiche (resign/draw/blunder).
 */
export function findBestMoveWithScore(board: BoardState, depth: number = 4): ScoredMove {
  return searchBest(board, depth);
}

/**
 * Valuta TUTTE le mosse legali alla radice (ognuna cercata a `depth-1`) e
 * restituisce le migliori `k` ordinate dalla migliore alla peggiore per il
 * giocatore di turno, con il loro score (prospettiva Bianco).
 *
 * Usato per i "blunder realistici": invece di una mossa casuale, il bot può
 * scegliere una mossa leggermente subottimale (2ª/3ª migliore), come un umano.
 * Nessun pruning alla radice → ogni mossa ha uno score affidabile. Chiamare
 * con una `depth` contenuta (è O(branching) ricerche).
 */
export function findBestMovesScored(
  board: BoardState,
  depth: number,
  k: number = 4,
): ScoredMove[] {
  const isMaximizing = board.currentTurn === Color.WHITE;
  const moves = getAllLegalMoves(board);
  if (moves.length === 0) return [];

  const scored = scoreRootMoves(
    orderMoves(moves, board),
    board,
    Math.max(0, depth - 1),
    isMaximizing,
    // Un contesto NUOVO per ogni radice: il budget è per mossa, non per la ricerca intera.
    () => ({
      startTime: Date.now(),
      timeLimit: TIME_LIMITS[depth] || 2000,
      nodesSearched: 0,
      timedOut: false,
    }),
    false,
  )!;
  return scored.slice(0, Math.max(1, k));
}

/**
 * Come `findBestMovesScored`, ma con un budget di tempo per l'INTERA ricerca e
 * iterative deepening sul set di radici: si scende di un ply alla volta e si tiene
 * l'ultima profondità **completata**.
 *
 * È la differenza che rende utilizzabili le profondità alte. `findBestMovesScored` dà
 * a ogni radice un contesto suo, quindi il tempo totale scala col fattore di
 * ramificazione e non ha tetto; e quando una radice sfora, il punteggio troncato (una
 * valutazione statica presa a metà albero) risale come se fosse valido. Qui il contesto
 * è uno solo: se scade, il giro in corso si butta via tutto insieme e si restituisce
 * quello precedente, che è completo per costruzione.
 */
export function findBestMovesScoredTimed(
  board: BoardState,
  maxDepth: number,
  k: number = 4,
  opts: ScoredSearchOptions,
): ScoredMoveSet {
  const isMaximizing = board.currentTurn === Color.WHITE;
  const moves = getAllLegalMoves(board);
  if (moves.length === 0) return { candidates: [], reachedDepth: 0, timedOut: false };

  const ordered = orderMoves(moves, board);
  const topK = Math.max(1, k);
  const startTime = Date.now();
  const ctx: SearchContext = {
    startTime,
    timeLimit: opts.timeBudgetMs,
    nodesSearched: 0,
    timedOut: false,
  };

  let candidates: ScoredMove[] = [];
  let reachedDepth = 0;
  let timedOut = false;
  let prevCost = 0;
  let growth = DEFAULT_PLY_GROWTH;

  for (let d = 2; d <= maxDepth; d += 1) {
    const iterStart = Date.now();
    const scored = scoreRootMoves(ordered, board, d - 1, isMaximizing, () => ctx, true);
    if (scored === null) {
      timedOut = true;
      break;
    }

    candidates = scored.slice(0, topK);
    reachedDepth = d;
    opts.onIteration?.(d, candidates);

    const cost = Date.now() - iterStart;
    if (prevCost >= GROWTH_SAMPLE_MIN_MS && cost > 0) {
      // Media mobile, non il rapporto secco fra le ultime due: un singolo campione oscilla
      // facilmente del doppio (carico della macchina, GC) e una sottostima fa partire un
      // ply che non finirà, buttando via il tempo che resta.
      const sample = Math.min(MAX_PLY_GROWTH, Math.max(MIN_PLY_GROWTH, cost / prevCost));
      growth = (growth + sample) / 2;
    }
    prevCost = cost;

    // Stop predittivo. Iniziare un ply che non può finire significa bruciare tutto il
    // tempo residuo per poi scartarne il risultato: senza questo, su una posizione fitta
    // un budget da 45s resta 45s per restituire la stessa profondità che ne chiedeva 17.
    // Resta una stima: se sbaglia per difetto si perde il tempo che avanza, mai la
    // correttezza del risultato — il giro incompleto viene comunque scartato.
    if (d < maxDepth && Date.now() - startTime + cost * growth > opts.timeBudgetMs) {
      timedOut = true;
      break;
    }
  }

  // Nemmeno il primo giro è arrivato in fondo: meglio i candidati ordinati con la
  // valutazione statica che una lista vuota.
  if (reachedDepth === 0) {
    candidates = ordered.slice(0, topK).map((move) => ({
      move,
      score: evaluateTerminal(applyMoveToBoard(board, move)),
    }));
  }

  return { candidates, reachedDepth, timedOut };
}

/**
 * Valuta ogni mossa di radice (già ordinata) a `childDepth` e ordina il risultato dal
 * punto di vista del giocatore di turno: per il Bianco score alto = meglio, per il Nero
 * il contrario.
 *
 * `ctxFor` decide la politica di tempo — un contesto per radice (budget per mossa) o
 * sempre lo stesso (budget globale). Con `abortOnTimeout` un contesto condiviso scaduto
 * interrompe e restituisce `null`: da lì in poi `minimax` torna subito con valutazioni
 * statiche, quindi l'intero giro è carta straccia.
 */
function scoreRootMoves(
  orderedMoves: Move[],
  board: BoardState,
  childDepth: number,
  isMaximizing: boolean,
  ctxFor: () => SearchContext,
  abortOnTimeout: boolean,
): ScoredMove[] | null {
  const scored: ScoredMove[] = [];

  for (const move of orderedMoves) {
    const ctx = ctxFor();
    const child = applyMoveToBoard(board, move);
    const result = minimax(child, childDepth, -INFINITY, INFINITY, !isMaximizing, ctx);
    if (abortOnTimeout && ctx.timedOut) return null;
    scored.push({ move, score: result.score });
  }

  scored.sort((a, b) => (isMaximizing ? b.score - a.score : a.score - b.score));
  return scored;
}

/**
 * Algoritmo Minimax con Alpha-Beta Pruning
 */
function minimax(
  board: BoardState,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizing: boolean,
  ctx: SearchContext
): SearchResult {
  ctx.nodesSearched++;

  // Check time limit ogni 512 nodi (potenza di 2 per efficienza)
  if ((ctx.nodesSearched & 511) === 0) {
    if (Date.now() - ctx.startTime > ctx.timeLimit) {
      ctx.timedOut = true;
      return { score: evaluatePosition(board), move: null, nodesSearched: 0 };
    }
  }

  // Controlla se la partita è finita
  if (board.status !== GameStatus.PLAYING) {
    return {
      score: evaluateTerminal(board),
      move: null,
      nodesSearched: 0,
    };
  }

  const moves = getAllLegalMoves(board);

  // Nessuna mossa disponibile = sconfitta — tranne in antidama, dove restare senza
  // mosse è la vittoria. (Ramo quasi irraggiungibile ora che applyMoveToBoard calcola
  // lo status dei figli: ci si arriva solo da una radice con status non aggiornato.)
  if (moves.length === 0) {
    const stuckSideLoses = (board.ruleMode ?? 'normal') !== 'antidraughts';
    const sideToMoveScore = stuckSideLoses ? -WIN_SCORE : WIN_SCORE;
    const score = isMaximizing ? sideToMoveScore : -sideToMoveScore;
    return { score, move: null, nodesSearched: 0 };
  }

  // Profondità zero: quiescence search (continua se ci sono catture)
  if (depth === 0) {
    return quiescenceSearch(board, alpha, beta, isMaximizing, ctx, 4);
  }

  // Ordina le mosse per migliorare il pruning
  const sortedMoves = orderMoves(moves, board);

  let bestMove: Move | null = null;

  if (isMaximizing) {
    let maxScore = -INFINITY;

    for (const move of sortedMoves) {
      if (ctx.timedOut) break;

      const newBoard = applyMoveToBoard(board, move);
      const result = minimax(newBoard, depth - 1, alpha, beta, false, ctx);

      if (result.score > maxScore) {
        maxScore = result.score;
        bestMove = move;
      }

      alpha = Math.max(alpha, result.score);
      if (beta <= alpha) {
        break; // Beta cutoff
      }
    }

    return { score: maxScore, move: bestMove, nodesSearched: 0 };
  } else {
    let minScore = INFINITY;

    for (const move of sortedMoves) {
      if (ctx.timedOut) break;

      const newBoard = applyMoveToBoard(board, move);
      const result = minimax(newBoard, depth - 1, alpha, beta, true, ctx);

      if (result.score < minScore) {
        minScore = result.score;
        bestMove = move;
      }

      beta = Math.min(beta, result.score);
      if (beta <= alpha) {
        break; // Alpha cutoff
      }
    }

    return { score: minScore, move: bestMove, nodesSearched: 0 };
  }
}

/**
 * Quiescence search: ai nodi foglia, continua a cercare catture
 * per evitare l'effetto orizzonte
 */
function quiescenceSearch(
  board: BoardState,
  alpha: number,
  beta: number,
  isMaximizing: boolean,
  ctx: SearchContext,
  maxDepth: number
): SearchResult {
  ctx.nodesSearched++;

  /*
   * Prima si guarda se la partita è FINITA, poi se la profondità è esaurita.
   *
   * L'ordine era invertito, e non è un dettaglio di stile: al nodo con
   * `maxDepth === 0` si restituiva la valutazione statica del materiale anche su
   * una posizione TERMINALE. Cioè una vittoria in fondo alla quiescenza valeva
   * «ho tre pezzi in più» (qualche centinaio di punti) invece di ±100000, e la
   * ricerca poteva preferirle una linea che guadagna materiale senza vincere.
   * Nei finali stretti — dove la quiescenza arriva in fondo ai suoi quattro
   * livelli — è la differenza fra chiudere e non chiudere.
   *
   * Uno stato terminale c'è davvero: `applyMoveToBoard` calcola lo `status` del
   * figlio, quindi i figli terminali arrivano qui già marcati.
   */
  if (board.status !== GameStatus.PLAYING) {
    return { score: evaluateTerminal(board), move: null, nodesSearched: 0 };
  }

  const standPat = evaluatePosition(board);

  if (maxDepth === 0) {
    return { score: standPat, move: null, nodesSearched: 0 };
  }

  if (isMaximizing) {
    if (standPat >= beta) {
      return { score: standPat, move: null, nodesSearched: 0 };
    }
    alpha = Math.max(alpha, standPat);
  } else {
    if (standPat <= alpha) {
      return { score: standPat, move: null, nodesSearched: 0 };
    }
    beta = Math.min(beta, standPat);
  }

  const moves = getAllLegalMoves(board);
  // Solo catture nella quiescence search — in Breakthrough anche le promozioni, perché
  // lì una promozione TRANQUILLA appena oltre l'orizzonte vince la partita all'istante e
  // il filtro sulle sole catture la renderebbe invisibile.
  const isBreakthrough = (board.ruleMode ?? 'normal') === 'breakthrough';
  const captures = moves.filter(m => m.captures.length > 0 || (isBreakthrough && m.isPromotion));

  if (captures.length === 0) {
    return { score: standPat, move: null, nodesSearched: 0 };
  }

  // Ordina catture per numero di pezzi catturati (più catture = meglio); in Breakthrough
  // la promozione viene prima di tutto, visto che chiude la partita.
  captures.sort((a, b) => {
    if (isBreakthrough && a.isPromotion !== b.isPromotion) return a.isPromotion ? -1 : 1;
    return b.captures.length - a.captures.length;
  });

  if (isMaximizing) {
    let maxScore = standPat;

    for (const move of captures) {
      if (ctx.timedOut) break;

      const newBoard = applyMoveToBoard(board, move);
      const result = quiescenceSearch(newBoard, alpha, beta, false, ctx, maxDepth - 1);

      maxScore = Math.max(maxScore, result.score);
      alpha = Math.max(alpha, result.score);
      if (beta <= alpha) break;
    }

    return { score: maxScore, move: null, nodesSearched: 0 };
  } else {
    let minScore = standPat;

    for (const move of captures) {
      if (ctx.timedOut) break;

      const newBoard = applyMoveToBoard(board, move);
      const result = quiescenceSearch(newBoard, alpha, beta, true, ctx, maxDepth - 1);

      minScore = Math.min(minScore, result.score);
      beta = Math.min(beta, result.score);
      if (beta <= alpha) break;
    }

    return { score: minScore, move: null, nodesSearched: 0 };
  }
}

/**
 * Ordina le mosse per migliorare l'alpha-beta pruning
 * Ordine: catture multiple > catture singole > promozioni > mosse centrali > resto
 */
function orderMoves(moves: Move[], board: BoardState): Move[] {
  // In antidama gli obiettivi sono rovesciati: catturare di più e promuovere sono le
  // mosse PEGGIORI, quindi vanno provate per ultime. Tocca solo l'ordine — cioè quanti
  // rami l'alpha-beta riesce a tagliare — mai il risultato della ricerca.
  const isAnti = (board.ruleMode ?? 'normal') === 'antidraughts';

  return [...moves].sort((a, b) => {
    // 1. Catture prima (più catture = priorità maggiore)
    if (a.captures.length !== b.captures.length) {
      return isAnti
        ? a.captures.length - b.captures.length
        : b.captures.length - a.captures.length;
    }

    // 2. Promozioni prima
    if (a.isPromotion !== b.isPromotion) {
      return (a.isPromotion ? -1 : 1) * (isAnti ? -1 : 1);
    }

    // 3. Mosse verso il centro (heuristica)
    const config = VARIANT_CONFIGS[board.variant];
    const boardSize = config.size;
    const allSquares = config.playableSquares === boardSize * boardSize;
    const centerA = distanceFromCenter(a.to, boardSize, allSquares);
    const centerB = distanceFromCenter(b.to, boardSize, allSquares);
    return centerA - centerB;
  });
}

/**
 * Distanza di una casella dal centro della scacchiera
 */
function distanceFromCenter(square: number, boardSize: number, allSquares: boolean): number {
  // All-squares (Turkish/Armenian, 1..N²) usano N² caselle; dark-only (International/Italian) N²/2.
  const totalSquares = allSquares ? boardSize * boardSize : (boardSize * boardSize) / 2;
  const center = totalSquares / 2;
  return Math.abs(square - center);
}

/**
 * Valuta una posizione terminale
 */
function evaluateTerminal(board: BoardState): number {
  switch (board.status) {
    case GameStatus.WHITE_WINS:
      return WIN_SCORE;
    case GameStatus.BLACK_WINS:
      return -WIN_SCORE;
    case GameStatus.DRAW:
      return 0;
    default:
      return evaluatePosition(board);
  }
}

/**
 * Applica una mossa e ritorna il nuovo stato
 * Versione leggera per la ricerca (non modifica l'originale)
 */
function applyMoveToBoard(board: BoardState, move: Move): BoardState {
  // Tipo del pezzo che muove PRIMA dell'eventuale promozione (serve a updateFrisianState).
  const movingPiece = board.pieces.find((p) => p.square === move.from);

  // Rimuovi pezzi catturati e muovi il pezzo
  const newPieces: Piece[] = [];

  for (const piece of board.pieces) {
    // Salta i pezzi catturati
    if (move.captures.some((c) => c.square === piece.square)) {
      continue;
    }

    // Muovi il pezzo
    if (piece.square === move.from) {
      newPieces.push({
        color: piece.color,
        type: move.isPromotion ? PieceType.KING : piece.type,
        square: move.to,
      });
    } else {
      newPieces.push(piece);
    }
  }

  const newTurn = board.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;

  // Crea nuovo stato
  const newBoard: BoardState = {
    pieces: newPieces,
    currentTurn: newTurn,
    status: GameStatus.PLAYING, // Verrà aggiornato sotto
    variant: board.variant,
    ruleMode: board.ruleMode,
    moveHistory: [...board.moveHistory, move],
    // La regola VERA, chiesta al motore e non ricopiata: si azzera anche sul
    // movimento di una PEDINA, non solo sulla cattura. Con metà regola la
    // ricerca vedeva arrivare la patta per non-progresso quando non era vera, e
    // a parità di posizione e profondità scegliva mosse diverse nei finali a
    // seconda del contatore di partenza.
    halfMoveClock: nextHalfMoveClock(board.halfMoveClock, move, movingPiece?.type ?? PieceType.MAN),
    positionHistory: [...(board.positionHistory ?? [])],
  };

  // Traccia posizione per rilevamento patta per ripetizione
  const positionHash = serializePosition(newBoard);
  newBoard.positionHistory!.push(positionHash);

  // Mantiene lo stato Frisian (kingMoveStreak + frisianEndgamePly). No-op per le altre varianti,
  // così la ricerca "vede" la dama bloccata e la patta 2-dame-vs-1 come il gioco reale.
  updateFrisianState(board, newBoard, move, movingPiece?.type ?? PieceType.MAN);

  // Stato terminale: un'unica fonte di verità con il gioco reale (evita che questa logica derivi
  // dal vero engine, com'è già successo con la guardia drawMoveLimit). ruleMode viene copiato
  // (riga 436) perché la ricerca sappia giocare Breakthrough e Antidraughts: senza, l'AI non
  // vedrebbe la promozione come vittoria e in antidama giocherebbe esattamente al contrario.
  // Le partite normali restano byte-identiche: determineGameStatus legge `ruleMode ?? 'normal'`
  // e confronta solo con le tre stringhe, quindi assente e 'normal' sono indistinguibili.
  newBoard.status = determineGameStatus(newBoard);

  return newBoard;
}
